<template>
  <div class="appearance-container">
    <div class="container py-4">
      <!-- Step Header -->
      <div class="step-header mb-4">
        <h2 class="text-center">Step 5: Customize Appearance</h2>
        <p class="text-center text-muted">
          Configure the look and feel of your MSDAT dashboard
        </p>
      </div>

      <div class="row">
        <!-- Left Column: Theme & Colors -->
        <div class="col-lg-6">
          <!-- Theme Selection Card -->
          <b-card class="theme-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-palette mr-2 text-primary"></i>
                <strong>Theme Selection</strong>
              </div>
            </template>

            <div class="theme-options">
              <div
                v-for="theme in themeOptions"
                :key="theme.value"
                class="theme-option"
                :class="{ active: form.theme === theme.value }"
                @click="selectTheme(theme.value)"
              >
                <div class="theme-preview" :class="theme.value">
                  <div class="theme-header"></div>
                  <div class="theme-body">
                    <div class="theme-sidebar"></div>
                    <div class="theme-content"></div>
                  </div>
                </div>
                <span class="theme-name">{{ theme.text }}</span>
              </div>
            </div>
          </b-card>

          <!-- Color Customization Card -->
          <b-card class="colors-card mb-4">
            <template #header>
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <i class="fas fa-tint mr-2 text-info"></i>
                  <strong>Custom Colors</strong>
                </div>
                <b-button variant="outline-secondary" size="sm" @click="resetColors">
                  <i class="fas fa-undo mr-1"></i>
                  Reset
                </b-button>
              </div>
            </template>

            <div class="color-inputs">
              <div class="color-input-group">
                <label>Primary Color</label>
                <div class="color-input-wrapper">
                  <input
                    type="color"
                    v-model="form.colors.primary"
                    class="color-input"
                  />
                  <input
                    type="text"
                    v-model="form.colors.primary"
                    class="form-control form-control-sm color-text"
                  />
                </div>
              </div>

              <div class="color-input-group">
                <label>Secondary Color</label>
                <div class="color-input-wrapper">
                  <input
                    type="color"
                    v-model="form.colors.secondary"
                    class="color-input"
                  />
                  <input
                    type="text"
                    v-model="form.colors.secondary"
                    class="form-control form-control-sm color-text"
                  />
                </div>
              </div>

              <div class="color-input-group">
                <label>Accent Color</label>
                <div class="color-input-wrapper">
                  <input
                    type="color"
                    v-model="form.colors.accent"
                    class="color-input"
                  />
                  <input
                    type="text"
                    v-model="form.colors.accent"
                    class="form-control form-control-sm color-text"
                  />
                </div>
              </div>

              <div class="color-input-group">
                <label>Background Color</label>
                <div class="color-input-wrapper">
                  <input
                    type="color"
                    v-model="form.colors.background"
                    class="color-input"
                  />
                  <input
                    type="text"
                    v-model="form.colors.background"
                    class="form-control form-control-sm color-text"
                  />
                </div>
              </div>

              <div class="color-input-group">
                <label>Text Color</label>
                <div class="color-input-wrapper">
                  <input
                    type="color"
                    v-model="form.colors.text"
                    class="color-input"
                  />
                  <input
                    type="text"
                    v-model="form.colors.text"
                    class="form-control form-control-sm color-text"
                  />
                </div>
              </div>
            </div>
          </b-card>

          <!-- Logo Upload Card -->
          <b-card class="logo-card">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-image mr-2 text-success"></i>
                <strong>Logo (Optional)</strong>
              </div>
            </template>

            <div class="logo-upload">
              <div v-if="form.logo" class="logo-preview">
                <img :src="form.logo" alt="Logo" class="uploaded-logo" />
                <b-button variant="outline-danger" size="sm" @click="removeLogo">
                  <i class="fas fa-times mr-1"></i>
                  Remove
                </b-button>
              </div>
              <div v-else class="logo-placeholder" @click="$refs.logoInput.click()">
                <i class="fas fa-cloud-upload-alt fa-2x text-muted mb-2"></i>
                <p class="text-muted small mb-0">Click to upload logo</p>
                <p class="text-muted small">(PNG, JPG, SVG)</p>
              </div>
              <input
                type="file"
                ref="logoInput"
                accept="image/*"
                class="d-none"
                @change="handleLogoUpload"
              />
            </div>
          </b-card>
        </div>

        <!-- Right Column: Preview -->
        <div class="col-lg-6">
          <b-card class="preview-card">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-eye mr-2 text-warning"></i>
                <strong>Live Preview</strong>
              </div>
            </template>

            <div class="preview-dashboard" :style="previewStyles">
              <!-- Preview Header -->
              <div class="preview-header">
                <div class="preview-logo" v-if="form.logo">
                  <img :src="form.logo" alt="Logo" />
                </div>
                <div class="preview-title">
                  <h5>{{ dashboardName }}</h5>
                  <p>{{ dashboardDescription }}</p>
                </div>
              </div>

              <!-- Preview Control Panel -->
              <div class="preview-controls">
                <div class="preview-control"></div>
                <div class="preview-control"></div>
                <div class="preview-control"></div>
              </div>

              <!-- Preview Content -->
              <div class="preview-content">
                <!-- Summary Cards -->
                <div class="preview-summary">
                  <div class="preview-stat" style="--stat-color: var(--primary-color)">
                    <span class="stat-icon"><i class="fas fa-chart-line"></i></span>
                    <span class="stat-value">{{ dataSummary?.indicatorCount || 0 }}</span>
                    <span class="stat-label">Indicators</span>
                  </div>
                  <div class="preview-stat" style="--stat-color: var(--accent-color)">
                    <span class="stat-icon"><i class="fas fa-map-marker-alt"></i></span>
                    <span class="stat-value">{{ dataSummary?.locationCount || 0 }}</span>
                    <span class="stat-label">Locations</span>
                  </div>
                  <div class="preview-stat" style="--stat-color: var(--secondary-color)">
                    <span class="stat-icon"><i class="fas fa-database"></i></span>
                    <span class="stat-value">{{ dataSummary?.totalRows || 0 }}</span>
                    <span class="stat-label">Data Points</span>
                  </div>
                </div>

                <!-- Chart Placeholder -->
                <div class="preview-chart">
                  <div class="chart-bars">
                    <div class="chart-bar" style="height: 60%"></div>
                    <div class="chart-bar" style="height: 80%"></div>
                    <div class="chart-bar" style="height: 45%"></div>
                    <div class="chart-bar" style="height: 90%"></div>
                    <div class="chart-bar" style="height: 70%"></div>
                  </div>
                </div>
              </div>
            </div>
          </b-card>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="navigation-buttons mt-4 d-flex justify-content-between">
        <b-button variant="outline-secondary" @click="goBack">
          <i class="fas fa-arrow-left mr-2"></i>
          Back to Column Mapping
        </b-button>
        <b-button variant="primary" @click="saveAndContinue">
          Continue to Settings
          <i class="fas fa-arrow-right ml-2"></i>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

export default {
  name: 'AppearanceConfig',

  data() {
    return {
      form: {
        theme: 'light',
        colors: {
          primary: '#007bff',
          secondary: '#6c757d',
          accent: '#28a745',
          background: '#ffffff',
          text: '#212529',
        },
        logo: null,
        fontFamily: 'Inter, sans-serif',
      },
      themeOptions: [
        { value: 'light', text: 'Light' },
        { value: 'dark', text: 'Dark' },
        { value: 'blue', text: 'Blue' },
        { value: 'green', text: 'Green' },
      ],
      defaultColors: {
        light: {
          primary: '#007bff',
          secondary: '#6c757d',
          accent: '#28a745',
          background: '#ffffff',
          text: '#212529',
        },
        dark: {
          primary: '#0d6efd',
          secondary: '#adb5bd',
          accent: '#20c997',
          background: '#212529',
          text: '#f8f9fa',
        },
        blue: {
          primary: '#0056b3',
          secondary: '#4a90d9',
          accent: '#17a2b8',
          background: '#f0f8ff',
          text: '#1a1a2e',
        },
        green: {
          primary: '#007D53',
          secondary: '#28a745',
          accent: '#20c997',
          background: '#f0fff4',
          text: '#1a2e1a',
        },
      },
    };
  },

  computed: {
    ...mapState('configurer', ['appearanceConfig', 'dashboardConfig', 'dataSummary']),

    dashboardName() {
      return this.dashboardConfig?.name || 'My MSDAT Dashboard';
    },

    dashboardDescription() {
      return this.dashboardConfig?.description || 'Health data visualization platform';
    },

    previewStyles() {
      return {
        '--primary-color': this.form.colors.primary,
        '--secondary-color': this.form.colors.secondary,
        '--accent-color': this.form.colors.accent,
        '--background-color': this.form.colors.background,
        '--text-color': this.form.colors.text,
      };
    },
  },

  mounted() {
    // Load existing appearance config
    if (this.appearanceConfig) {
      this.form = {
        ...this.form,
        ...this.appearanceConfig,
        colors: {
          ...this.form.colors,
          ...(this.appearanceConfig.colors || {}),
        },
      };
    }
  },

  methods: {
    ...mapMutations('configurer', ['SET_APPEARANCE_CONFIG']),

    selectTheme(themeName) {
      this.form.theme = themeName;
      if (this.defaultColors[themeName]) {
        this.form.colors = { ...this.defaultColors[themeName] };
      }
    },

    resetColors() {
      const themeColors = this.defaultColors[this.form.theme] || this.defaultColors.light;
      this.form.colors = { ...themeColors };
    },

    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.form.logo = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    removeLogo() {
      this.form.logo = null;
      if (this.$refs.logoInput) {
        this.$refs.logoInput.value = '';
      }
    },

    goBack() {
      this.$router.push({ name: 'configure-mapping' });
    },

    saveAndContinue() {
      // Save appearance config to store
      this.SET_APPEARANCE_CONFIG(this.form);

      // Navigate to settings
      this.$router.push({ name: 'configure-settings' });
    },
  },
};
</script>

<style scoped>
.appearance-container {
  min-height: 100vh;
  /* background-color: #f8f9fa; Removed */
  padding-bottom: 2rem;
}

.step-header h2 {
  color: #2c3e50;
  font-weight: 700;
}

/* Glass Cards */
.theme-card,
.colors-card,
.logo-card,
.preview-card {
  border: none;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
  margin-bottom: 24px;
}

::v-deep .card-header {
  background-color: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1.25rem;
  border-radius: 20px 20px 0 0 !important;
}

/* Theme Options */
.theme-options {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.theme-option {
  cursor: pointer;
  text-align: center;
  padding: 0.75rem;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.5);
}

.theme-option:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.8);
}

.theme-option.active {
  border-color: #007D53;
  background-color: rgba(0, 125, 83, 0.05);
  box-shadow: 0 4px 12px rgba(0, 125, 83, 0.15);
}

.theme-preview {
  width: 100%;
  height: 70px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}

.theme-name {
    font-weight: 600;
    font-size: 0.9rem;
    color: #2c3e50;
}

/* Color Inputs */
.color-inputs {
  display: grid;
  gap: 1.2rem;
}

.color-input-group label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #57606f;
  margin-bottom: 0.5rem;
}

.color-input-wrapper {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  background: rgba(255, 255, 255, 0.5);
  padding: 0.5rem;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.05);
}

.color-input {
  width: 45px;
  height: 45px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  padding: 0;
  background: none;
}

.color-text {
  flex: 1;
  font-family: 'JetBrains Mono', monospace;
  text-transform: uppercase;
  background: transparent !important;
  border: none !important;
  font-size: 0.95rem;
  color: #2c3e50;
}

/* Logo Upload */
.logo-placeholder {
  padding: 2.5rem;
  border: 2px dashed rgba(0, 125, 83, 0.3);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.4);
}

.logo-placeholder:hover {
  border-color: #007D53;
  background-color: rgba(0, 125, 83, 0.05);
}

/* Preview Dashboard */
.preview-dashboard {
  background-color: var(--background-color);
  color: var(--text-color);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.08); /* More subtle border */
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.preview-header {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  padding: 1.25rem;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.preview-title h5 { font-weight: 700; letter-spacing: -0.5px; }

.preview-controls {
  padding: 0.75rem 1rem;
  background-color: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.preview-stat {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: none;
    background: #fff;
}

.preview-chart {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: none;
    background: #fff;
    height: 140px;
}

/* Navigation */
.navigation-buttons {
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background-color: rgba(255, 255, 255, 0.5);
  margin-top: 2rem;
  border-radius: 0 0 20px 20px;
  backdrop-filter: blur(10px);
}

::v-deep .btn-primary {
  background-color: #007D53;
  border-color: #007D53;
  border-radius: 10px;
  padding: 0.5rem 1.5rem;
}

::v-deep .btn-outline-secondary {
    border-radius: 10px;
    border-color: #b2bec3;
    color: #636e72;
}
</style>
```
